import { ALL_BRANCHES, ContextSession, OnboardingStatus } from './types';

export type ShimParams = { orgSlug: string; restaurantId?: string; branchId?: string; leaf?: string };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

/**
 * Deep-link shim decision. Params must already be UUIDs (ContextLoader maps
 * codes first). Returns the short path to redirect to, or 'unresolvable' when
 * the link points at a scope the user cannot reach — the caller then falls
 * back to the app root, where resolveContext picks defaults.
 */
export function resolveShim(input: {
  session: ContextSession | null;
  status: OnboardingStatus | null;
  params: ShimParams;
  canAggregate: boolean;
  /**
   * True when the URL carried a restaurant/branch code that mapped to nothing. Without
   * it an unknown code is indistinguishable from "the URL had no restaurant segment",
   * and the link would quietly resolve to whatever the previous cookie held.
   */
  unresolved?: boolean;
}): { kind: 'redirect'; to: string } | { kind: 'unresolvable' } {
  const { session, status, params, canAggregate, unresolved = false } = input;

  if (!session) {
    return { kind: 'redirect', to: `/sign-in?returnTo=${encodeURIComponent(currentShimPath(params))}` };
  }
  if (!session.emailVerified) {
    return { kind: 'redirect', to: '/verify-email' };
  }
  if (!status || !status.hasOrg || !status.hasRestaurant || !status.hasBranch) {
    return { kind: 'redirect', to: '/onboarding' };
  }
  if (params.orgSlug !== status.organizationSlug) {
    return { kind: 'unresolvable' };
  }
  if (unresolved) {
    return { kind: 'unresolvable' };
  }
  if (!params.restaurantId || !params.branchId) {
    return { kind: 'redirect', to: shimLeaf(params.leaf) };
  }
  if (params.branchId === ALL_BRANCHES && !canAggregate) {
    return { kind: 'unresolvable' };
  }
  return { kind: 'redirect', to: shimLeaf(params.leaf) };
}

function currentShimPath(params: ShimParams): string {
  const rest: string[] = [];
  if (params.restaurantId) rest.push(`r/${params.restaurantId}`);
  if (params.branchId) rest.push(`b/${params.branchId}`);
  if (params.leaf) rest.push(params.leaf);
  return `/o/${params.orgSlug}${rest.length > 0 ? `/${rest.join('/')}` : ''}`;
}

function shimLeaf(leaf?: string): string {
  return leaf ? `/${leaf.replace(/^\//, '')}` : '/dashboard/selling';
}
